import type {
    ComponentStageData,
    ComponentStageHeuristicDefinition,
    PluginContext,
    ReadOnlyExtensionAPI
} from '../../types'
import {
    isBoxPlugin,
    isChildOfHeader,
    isHeaderContainer,
    isHeaderFirstRowChildComponent,
    isInternalBox,
    isMenuContainerChild,
    isPinned,
    isPinnedToContainer,
    isSection,
    isSectionsRootContainer,
    isSuperGridCell,
    updateTargetLayout
} from '../common'
import {
    getMeasurementsPointer,
    getSourceLayoutPointer,
    getStructurePointer,
    getTargetLayoutPointer,
    getVirtualStructurePointer
} from '../../conversionDal/utils/pointer'
import {
    BOTTOM_PADDING,
    DEFAULT_PADDING_BOTTOM,
    HeightType,
    LIST_ITEM_PADDING_TOP,
    TOP_BOTTOM_RELATION,
    TOP_PADDING,
    HEADER_COMPONENTS_HEIGHT
} from './constants'
import {SemanticTypes} from '../../preprocess/conversionDataBuilders'
import {VIRTUAL_TAG} from '../grouping/constants'
import _ from 'lodash'
import type {HorizontalScaleAndSizePluginAPI} from '../horizontalScaleAndSize/types'

const updatePinnedVerticalMargins = (componentStageData: ComponentStageData) => {
    const {conversionDal, componentPointer} = componentStageData
    const {itemLayout} = conversionDal.get(getSourceLayoutPointer(componentPointer))
    const {itemLayout: targetItemLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer))
    const {margins, alignSelf, justifySelf} = itemLayout
    const {top, bottom} = margins

    updateTargetLayout(componentStageData, {
        itemLayout: _.merge({}, targetItemLayout, {
            alignSelf,
            justifySelf,
            margins: {
                top,
                bottom
            },
            type: 'FixedItemLayout'
        })
    })
}

//TODO: change this once we have a way heuristic to pinned containers
const updatePinnedToContainerVerticalMargins = (componentStageData: ComponentStageData) => {
    const {conversionDal, componentPointer} = componentStageData
    const {itemLayout} = conversionDal.get(getSourceLayoutPointer(componentPointer))
    const {itemLayout: targetItemLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer))
    const {margins, alignSelf, justifySelf} = itemLayout
    const {top, bottom} = margins

    updateTargetLayout(componentStageData, {
        itemLayout: _.merge({}, targetItemLayout, {
            alignSelf,
            justifySelf,
            margins: {
                top,
                bottom
            },
            type: 'GridItemLayout'
        })
    })
}

const updateComponentHeight = (
    componentStageData: ComponentStageData,
    height: number,
    heightType: HeightType = HeightType.Fixed
) => {
    const {conversionDal, componentPointer} = componentStageData
    const {componentLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {}

    if (heightType === HeightType.Auto) {
        updateTargetLayout(componentStageData, {
            componentLayout: {
                ...componentLayout,
                height: {
                    type: 'auto'
                },
                minHeight: {
                    value: height,
                    type: 'spx'
                },
                type: 'ComponentLayout'
            }
        })
    } else if (heightType === HeightType.AspectRatio) {
        updateTargetLayout(componentStageData, {
            componentLayout: {
                ...componentLayout,
                height: {
                    type: 'aspectRatio',
                    value: height
                },
                type: 'ComponentLayout'
            }
        })
    } else {
        updateTargetLayout(componentStageData, {
            componentLayout: {
                ...componentLayout,
                height: {
                    value: height,
                    type: 'spx'
                },
                type: 'ComponentLayout'
            }
        })
    }
}

const updateComponentVerticalMargins = (componentStageData: ComponentStageData, topMargin: number) => {
    const {conversionDal, componentPointer} = componentStageData
    const {itemLayout} = _.cloneDeep(conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {})
    itemLayout.margins = {
        ...itemLayout.margins,
        top: {
            value: topMargin,
            type: 'spx'
        },
        bottom: {
            value: 0,
            type: 'spx'
        }
    }

    updateTargetLayout(componentStageData, {
        itemLayout
    })
}

const updatePaddingBottom = (componentStageData: ComponentStageData, padding: number) => {
    const {conversionDal, componentPointer} = componentStageData
    const {containerLayout} = _.cloneDeep(conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {})
    _.set(containerLayout, ['padding', 'bottom'], {
        value: padding,
        type: 'spx'
    })

    updateTargetLayout(componentStageData, {
        containerLayout
    })
}

const getBottomPadding = (componentStageData: ComponentStageData) => {
    if (isSectionsRootContainer(componentStageData)) {
        return BOTTOM_PADDING.ROOT
    }

    return BOTTOM_PADDING.DESCENDANT
}

const setFitToContentHeightWithPadding = (componentStageData: ComponentStageData) => {
    updatePaddingBottom(componentStageData, getBottomPadding(componentStageData))
    updateComponentHeight(componentStageData, 0, HeightType.Auto)
}

const setMinHeightToEmptyContainer = (componentStageData: ComponentStageData, height: number) => {
    const {conversionDal, componentPointer} = componentStageData
    const {containerLayout} = _.cloneDeep(conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {})
    _.set(
        containerLayout,
        ['rows'],
        [
            {
                type: 'MinMaxSize',
                min: {
                    value: height,
                    type: 'spx'
                },
                max: {
                    type: 'auto'
                }
            }
        ]
    )

    updateTargetLayout(componentStageData, {
        containerLayout
    })
    updateComponentHeight(componentStageData, 0, HeightType.Auto)
}

const calculateAspectRatioHeight = (
    componentStageData: ComponentStageData,
    readOnlyExtensionAPI: ReadOnlyExtensionAPI
) => {
    const {componentPointer, initialSnapshot, globalContext} = componentStageData
    const componentMeasurementsPointer = getMeasurementsPointer(componentPointer)
    const originalMeasurements = initialSnapshot.getValue(componentMeasurementsPointer)
    const {horizontalScaleAndSize} = readOnlyExtensionAPI.getPluginsApi(globalContext)
    const width = (horizontalScaleAndSize as HorizontalScaleAndSizePluginAPI).getWidth(componentPointer)

    return originalMeasurements.height * (width / originalMeasurements.width)
}

const setAspectRatioHeight = (componentStageData: ComponentStageData) => {
    const {componentPointer, initialSnapshot} = componentStageData
    const componentMeasurementsPointer = getMeasurementsPointer(componentPointer)
    const originalMeasurements = initialSnapshot.getValue(componentMeasurementsPointer)
    const heightToWidthRatio = originalMeasurements.height / originalMeasurements.width
    updateComponentHeight(componentStageData, heightToWidthRatio, HeightType.AspectRatio)
}

const setImageHeight = (
    componentStageData: ComponentStageData,
    readOnlyExtensionAPI: ReadOnlyExtensionAPI,
    {
        minHeight,
        maxHeight,
        keepAspectRatio
    }: {
        minHeight: number
        maxHeight: number
        keepAspectRatio?: boolean
    }
) => {
    const {initialSnapshot, componentPointer, globalContext} = componentStageData
    const componentMeasurementsPointer = getMeasurementsPointer(componentPointer)
    const {height} = initialSnapshot.getValue(componentMeasurementsPointer)
    const {horizontalScaleAndSize} = readOnlyExtensionAPI.getPluginsApi(globalContext)
    const width = (horizontalScaleAndSize as HorizontalScaleAndSizePluginAPI).getWidth(componentPointer)

    if (keepAspectRatio) {
        const aspectRatioHeight = calculateAspectRatioHeight(componentStageData, readOnlyExtensionAPI)
        const heightToWidthRatio = Math.max(Math.min(aspectRatioHeight, maxHeight), minHeight) / width
        return updateComponentHeight(componentStageData, heightToWidthRatio, HeightType.AspectRatio)
    }

    const heightToWidthRatio = Math.max(Math.min(height, maxHeight), minHeight) / width
    updateComponentHeight(componentStageData, heightToWidthRatio, HeightType.AspectRatio)
}

const setOriginalHeight = (componentStageData: ComponentStageData) => {
    const {initialSnapshot, componentPointer} = componentStageData

    updateComponentHeight(componentStageData, initialSnapshot.getValue(getMeasurementsPointer(componentPointer)).height)
}

const genericBetweenCompTypesPredicate =
    (lowerCompSemanticType: string, upperCompSemanticType: string) => (componentStageData: ComponentStageData) => {
        const {componentPointer, conversionDal} = componentStageData
        const component = conversionDal.get(componentPointer)
        const containerPointer = getStructurePointer({id: component.parent})
        const container = conversionDal.get(containerPointer)
        const currentCompIndex = container.components!.indexOf(componentPointer.id)
        const previousSiblingPointer = getStructurePointer({
            id: container.components![currentCompIndex - 1]
        })
        const previousSibling = conversionDal.get(previousSiblingPointer)

        return (
            component.semanticType === lowerCompSemanticType && previousSibling.semanticType === upperCompSemanticType
        )
    }

const getGenericYHeuristic = (): ComponentStageHeuristicDefinition[] => {
    const heuristics = []

    for (const [lowerCompSemanticType, settings] of Object.entries(TOP_BOTTOM_RELATION)) {
        for (const [upperSemanticCompType, paddingValue] of Object.entries(settings)) {
            heuristics.push({
                predicate: genericBetweenCompTypesPredicate(lowerCompSemanticType, upperSemanticCompType),
                apply: (componentStageData: ComponentStageData) =>
                    updateComponentVerticalMargins(componentStageData, paddingValue),
                heuristicCode: `${lowerCompSemanticType} TOP MARGIN FROM ${upperSemanticCompType}`
            })
        }
    }

    return heuristics
}

export const heightHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) =>
            isMenuContainerChild(componentPointer, conversionDal),
        apply: (componentStageData: ComponentStageData) => {
            updateComponentHeight(componentStageData, 0, HeightType.Auto)
        },
        heuristicCode: 'MENU CONTAINER CHILD'
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            return isHeaderContainer(componentStageData)
        },
        apply: (componentStageData: ComponentStageData) => {
            updateComponentHeight(componentStageData, 70, HeightType.Auto)
        },
        heuristicCode: 'INTERNAL CONTAINER IN HEADER'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)
            return isChildOfHeader(componentPointer, conversionDal) && component.semanticType === SemanticTypes.MENU
        },
        apply: (componentStageData: ComponentStageData) =>
            updateComponentHeight(componentStageData, HEADER_COMPONENTS_HEIGHT.MENU_HEIGHT),
        heuristicCode: 'MENU IN HEADER'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)
            return isChildOfHeader(componentPointer, conversionDal) && component.semanticType === SemanticTypes.LOGO
        },
        apply: (componentStageData: ComponentStageData) =>
            updateComponentHeight(componentStageData, HEADER_COMPONENTS_HEIGHT.LOGO_HEIGHT),
        heuristicCode: 'LOGO IN HEADER'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return isChildOfHeader(componentPointer, conversionDal)
        },
        apply: (componentStageData: ComponentStageData) =>
            updateComponentHeight(componentStageData, 0, HeightType.Auto),
        heuristicCode: 'COMPONENT IN HEADER'
    },

    {
        predicate: (componentStageData: ComponentStageData) => isSection(componentStageData),
        apply: (componentStageData: ComponentStageData) =>
            updateComponentHeight(componentStageData, 0, HeightType.Auto),
        heuristicCode: 'SECTION AUTO HEIGHT'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)

            return component.semanticType === SemanticTypes.BOX
        },
        apply: (componentStageData: ComponentStageData) =>
            updateComponentHeight(componentStageData, 0, HeightType.Auto),
        heuristicCode: 'BOX AUTO HEIGHT'
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            const {componentPointer, conversionDal} = componentStageData
            const component = conversionDal.get(componentPointer)

            return isSectionsRootContainer(componentStageData) && !component.components?.length
        },
        apply: (componentStageData: ComponentStageData) => setMinHeightToEmptyContainer(componentStageData, 200),
        heuristicCode: 'EMPTY ROOT CONTAINER HEIGHT'
    },
    {
        predicate: isSectionsRootContainer,
        apply: setFitToContentHeightWithPadding,
        heuristicCode: 'ROOT CONTAINER FIT TO CONTENT HEIGHT'
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            const {componentPointer, conversionDal, initialSnapshot} = componentStageData
            const componentMeasurementsPointer = getMeasurementsPointer(componentPointer)
            const component = conversionDal.get(componentPointer)
            const originalMeasurements = initialSnapshot.getValue(componentMeasurementsPointer)

            return (
                (isInternalBox(componentStageData) || isBoxPlugin(componentStageData)) &&
                !component.components?.length &&
                originalMeasurements.height < 101
            )
        },
        apply: (componentStageData: ComponentStageData) => setMinHeightToEmptyContainer(componentStageData, 100),
        heuristicCode: 'EMPTY SMALL BOX HEIGHT'
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            const {componentPointer, conversionDal} = componentStageData
            const component = conversionDal.get(componentPointer)

            return (
                (isInternalBox(componentStageData) || isBoxPlugin(componentStageData)) && !component.components?.length
            )
        },
        apply: (componentStageData: ComponentStageData) => setMinHeightToEmptyContainer(componentStageData, 200),
        heuristicCode: 'EMPTY LARGE BOX HEIGHT'
    },
    {
        predicate: (componentStageData: ComponentStageData) =>
            isInternalBox(componentStageData) || isBoxPlugin(componentStageData),
        apply: (componentStageData: ComponentStageData) => setFitToContentHeightWithPadding(componentStageData),
        heuristicCode: 'BOX FIT TO CONTENT HEIGHT'
    },
    {
        predicate: ({componentPointer, conversionDal, initialSnapshot}: ComponentStageData) => {
            const componentMeasurementsPointer = getMeasurementsPointer(componentPointer)
            const {width} = initialSnapshot.getValue(componentMeasurementsPointer)
            const component = conversionDal.get(componentPointer)

            return component.semanticType === SemanticTypes.IMAGE && width < 201
        },
        apply: (
            componentStageData: ComponentStageData,
            _contextHelper: PluginContext,
            readOnlyExtensionAPI: ReadOnlyExtensionAPI
        ) => setImageHeight(componentStageData, readOnlyExtensionAPI, {minHeight: 48, maxHeight: 600}),
        heuristicCode: 'NARROW IMAGE HEIGHT'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)

            return component.semanticType === SemanticTypes.IMAGE
        },
        apply: (
            componentStageData: ComponentStageData,
            _contextHelper: PluginContext,
            readOnlyExtensionAPI: ReadOnlyExtensionAPI
        ) =>
            setImageHeight(componentStageData, readOnlyExtensionAPI, {
                minHeight: 48,
                maxHeight: 300,
                keepAspectRatio: true
            }),
        heuristicCode: 'IMAGE FALLBACK HEIGHT'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)

            return (
                component.semanticType === SemanticTypes.SVG || component.semanticType === SemanticTypes.EMBEDDED_MEDIA
            )
        },
        apply: setAspectRatioHeight,
        heuristicCode: 'ASPECT RATIO HEIGHT'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)

            return component.semanticType === SemanticTypes.TEXT
        },
        apply: (componentStageData: ComponentStageData) =>
            updateComponentHeight(componentStageData, 0, HeightType.Auto),
        heuristicCode: 'TEXT FALLBACK HEIGHT'
    },
    {
        predicate: () => true,
        apply: setOriginalHeight,
        heuristicCode: 'ANY COMPONENT FALLBACK HEIGHT'
    }
]

export const verticalMarginsHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: (componentStageData: ComponentStageData) => isSection(componentStageData),
        apply: () => {}
    },
    {
        predicate: (componentStageData: ComponentStageData) => isPinned(componentStageData),
        apply: (componentStageData: ComponentStageData) => {
            //TODO: change this once we have a way heuristic to pinned containers
            if (isPinnedToContainer(componentStageData)) {
                updatePinnedToContainerVerticalMargins(componentStageData)
                return
            }
            updatePinnedVerticalMargins(componentStageData)
        },
        heuristicCode: 'Pinned Element'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) =>
            isMenuContainerChild(componentPointer, conversionDal),
        apply: (componentStageData: ComponentStageData) => {
            updateComponentVerticalMargins(componentStageData, HEADER_COMPONENTS_HEIGHT.COMPONENT_IN_MENU_TOP_MARGIN)
        },
        heuristicCode: 'MENU CONTAINER CHILD'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return isHeaderFirstRowChildComponent(componentPointer, conversionDal)
        },
        apply: (componentStageData: ComponentStageData) =>
            updateComponentVerticalMargins(componentStageData, HEADER_COMPONENTS_HEIGHT.COMPONENT_IN_HEADER_TOP_MARGIN),
        heuristicCode: 'COMPONENT IN HEADER FIRST ROW'
    },
    {
        predicate: isSuperGridCell,
        apply: (componentStageData: ComponentStageData) => updateComponentVerticalMargins(componentStageData, 0),
        heuristicCode: '0 VERTICAL MARGINS FOR SUPERGRID CELL'
    },
    {
        predicate: (componentStageData: ComponentStageData) =>
            isInternalBox(componentStageData) || isBoxPlugin(componentStageData),
        apply: _.noop
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            const {componentPointer, conversionDal} = componentStageData
            const component = conversionDal.get(componentPointer)
            const containerPointer = getStructurePointer({id: component.parent})
            const container = conversionDal.get(containerPointer)
            const currentCompIndex = container.components!.indexOf(componentPointer.id)

            return (
                isSectionsRootContainer({componentPointer: containerPointer, conversionDal}) && currentCompIndex === 0
            )
        },
        apply: (componentStageData: ComponentStageData) =>
            updateComponentVerticalMargins(componentStageData, TOP_PADDING.ROOT),
        heuristicCode: 'FIRST CHILD IN SECTION TOP MARGIN'
    },
    {
        predicate: (componentStageData: ComponentStageData) => {
            const {componentPointer, conversionDal} = componentStageData
            const component = conversionDal.get(componentPointer)
            const containerPointer = getStructurePointer({id: component.parent})
            const container = conversionDal.get(containerPointer)
            const currentCompIndex = container.components!.indexOf(componentPointer.id)

            return currentCompIndex === 0 && isInternalBox({componentPointer: containerPointer, conversionDal})
        },
        apply: (componentStageData: ComponentStageData) =>
            updateComponentVerticalMargins(componentStageData, TOP_PADDING.DESCENDANT),
        heuristicCode: 'FIRST CHILD IN BOX TOP MARGIN'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)
            const virtualParent = conversionDal.get(getVirtualStructurePointer({id: component.virtualParentId}))

            return (
                virtualParent &&
                virtualParent.type === VIRTUAL_TAG &&
                virtualParent.components!.indexOf(componentPointer.id) === 0
            )
        },
        apply: (componentStageData: ComponentStageData) =>
            updateComponentVerticalMargins(componentStageData, LIST_ITEM_PADDING_TOP),
        heuristicCode: 'FIRST CHILD IN VIRTUAL LIST ITEM TOP MARGIN'
    },
    ...getGenericYHeuristic(),
    {
        predicate: () => true,
        apply: (componentStageData: ComponentStageData) =>
            updateComponentVerticalMargins(componentStageData, DEFAULT_PADDING_BOTTOM),
        heuristicCode: 'DEFAULT TOP MARGIN'
    }
]
